import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { Box, Chip, Divider, Link, useTheme } from '@mui/material'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdClose } from 'react-icons/md'
import { IProductCategoryDropDown } from 'src/api/product-category'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import {
  getPositionDrawingActiveFiltersRowSx,
  getPositionDrawingChipSx,
  getPositionDrawingClearAllSx,
  getPositionDrawingFilterDividerSx,
} from './positionDrawingFilterStyles'
import { getProductCategoryDisplayName, reorderFilterCategoryIds } from './positionDrawingFilterUtils'

interface PositionDrawingFilterChipListProps {
  productCategoryIds: EntityId[]
  categoryById: Map<string, IProductCategoryDropDown>
  onReorder: (categoryIds: EntityId[]) => void
  onRemoveAt: (index: number) => void
  onClearAll: () => void
}

export const PositionDrawingFilterChipList: React.FC<PositionDrawingFilterChipListProps> = ({
  productCategoryIds,
  categoryById,
  onReorder,
  onRemoveAt,
  onClearAll,
}) => {
  const { t } = useTranslation(['common'])
  const theme = useTheme()
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null)

  const handleDrop = useCallback(
    (toIndex: number) => {
      if (draggedIndex === null) return
      onReorder(reorderFilterCategoryIds(productCategoryIds, draggedIndex, toIndex))
      setDraggedIndex(null)
      setDropTargetIndex(null)
    },
    [draggedIndex, onReorder, productCategoryIds],
  )

  if (productCategoryIds.length === 0) return null

  return (
    <Box sx={getPositionDrawingActiveFiltersRowSx(theme)}>
      {productCategoryIds.map((categoryId, index) => {
        const category = categoryById.get(String(categoryId))
        const label = category ? getProductCategoryDisplayName(category) : String(categoryId)

        const isDragging = draggedIndex === index
        const isDropTarget = dropTargetIndex === index && draggedIndex !== null && draggedIndex !== index

        return (
          <Box
            key={`${categoryId}-${index}`}
            onDragOver={e => {
              e.preventDefault()
              e.dataTransfer.dropEffect = 'move'
              setDropTargetIndex(index)
            }}
            onDragLeave={() => {
              setDropTargetIndex(current => (current === index ? null : current))
            }}
            onDrop={e => {
              e.preventDefault()
              handleDrop(index)
            }}
            sx={{
              opacity: isDragging ? 0.55 : 1,
              outline: isDropTarget
                ? `1px dashed ${getThemeColor(theme, MUIThemeColors.white)}`
                : 'none',
              outlineOffset: 1,
              borderRadius: '14px',
            }}
          >
            <Chip
              size="small"
              label={label}
              icon={
                <Box
                  component="span"
                  draggable
                  onDragStart={e => {
                    e.stopPropagation()
                    setDraggedIndex(index)
                    e.dataTransfer.effectAllowed = 'move'
                    e.dataTransfer.setData('text/plain', String(index))
                  }}
                  onDragEnd={() => {
                    setDraggedIndex(null)
                    setDropTargetIndex(null)
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'grab',
                    color: getThemeColor(theme, MUIThemeColors.white),
                    '&:active': { cursor: 'grabbing' },
                  }}
                  aria-label="Drag to reorder"
                >
                  <DragIndicatorIcon sx={{ fontSize: '14px !important' }} />
                </Box>
              }
              onDelete={() => onRemoveAt(index)}
              deleteIcon={<MdClose size={14} />}
              sx={getPositionDrawingChipSx(theme)}
            />
          </Box>
        )
      })}
      <Divider orientation="vertical" flexItem sx={getPositionDrawingFilterDividerSx(theme)} />
      <Link component="button" type="button" variant="body2" onClick={onClearAll} sx={getPositionDrawingClearAllSx(theme)}>
        {t('clear-all', { ns: 'common' })}
      </Link>
    </Box>
  )
}
